import { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { analyticsApi } from '../../api/client';
import { Icon, type IconName } from '../../components/Icon';

interface StatItem {
  label: string;
  value: string | number;
  icon: IconName;
  color: string;
}

const QTYPE_LABEL: Record<string, string> = {
  single: '单选', multiple: '多选', judge: '判断', deep: '深度',
};

export function StatsPage() {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    analyticsApi.getStats()
      .then(data => setStats(data))
      .catch(e => setError(e instanceof Error ? e.message : '加载失败'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-state"><span className="spinner" /></div>;
  if (error) return <div className="page"><p style={{ padding: 24, textAlign: 'center', color: 'var(--color-danger)' }}>{error}</p></div>;
  if (!stats) return <div className="page"><p style={{ padding: 24, textAlign: 'center', color: 'var(--gray-400)' }}>暂无数据</p></div>;

  const items: StatItem[] = [
    { label: '学习天数', value: stats.days ?? 0, icon: 'calendar', color: '#3b82f6' },
    { label: '答题数', value: stats.totalAnswers ?? 0, icon: 'edit', color: '#8b5cf6' },
    { label: '正确数', value: stats.totalCorrect ?? 0, icon: 'check', color: '#059669' },
    { label: '正确率', value: `${((stats.cumulativeRate ?? 0) * 100).toFixed(1)}%`, icon: 'target', color: '#f59e0b' },
    { label: '完成套题', value: stats.attempts ?? 0, icon: 'book-open', color: '#ec4899' },
    { label: '企业排名', value: stats.rank ? `#${stats.rank}` : '未上榜', icon: 'trophy', color: '#f97316' },
  ];

  const cumulativeRate = (stats.cumulativeRate ?? 0) * 100;
  const daily = Array.isArray(stats.daily) ? stats.daily : [];
  const qtypeStats = Array.isArray(stats.qtypeStats) ? stats.qtypeStats : [];

  const gaugeOption = {
    series: [{
      type: 'gauge', startAngle: 210, endAngle: -30, min: 0, max: 100,
      radius: '100%', center: ['50%', '58%'],
      progress: { show: true, width: 12, itemStyle: { color: '#4f46e5' } },
      axisLine: { lineStyle: { width: 12, color: [[1, '#eef2ff']] } },
      axisTick: { show: false }, splitLine: { show: false }, axisLabel: { show: false },
      pointer: { show: false }, anchor: { show: false },
      detail: { valueAnimation: true, offsetCenter: [0, '-6%'], formatter: '{value}%', color: '#4f46e5', fontSize: 28, fontWeight: 700 },
      data: [{ value: Math.round(cumulativeRate) }],
    }],
  };

  const trendOption = {
    grid: { left: 32, right: 12, top: 20, bottom: 24 },
    tooltip: { trigger: 'axis' },
    legend: { data: ['答题数', '答对数'], top: 0, textStyle: { fontSize: 11 } },
    xAxis: {
      type: 'category',
      data: daily.map((d: any) => (d.date || '').slice(5)),
      axisLabel: { fontSize: 10, color: '#999' },
      axisLine: { lineStyle: { color: '#e5e7eb' } },
    },
    yAxis: {
      type: 'value',
      axisLabel: { fontSize: 10, color: '#999' },
      splitLine: { lineStyle: { color: '#f1f5f9' } },
    },
    series: [
      { name: '答题数', type: 'line', smooth: true, data: daily.map((d: any) => d.answers || 0), itemStyle: { color: '#4f46e5' }, areaStyle: { opacity: 0.15 } },
      { name: '答对数', type: 'line', smooth: true, data: daily.map((d: any) => d.correct || 0), itemStyle: { color: '#10b981' }, areaStyle: { opacity: 0.1 } },
    ],
  };

  const qtypeOption = qtypeStats.length === 0 ? null : {
    grid: { left: 8, right: 44, top: 8, bottom: 8, containLabel: true },
    tooltip: { trigger: 'axis' },
    xAxis: { type: 'value', max: 100, axisLabel: { fontSize: 10, color: '#999', formatter: '{value}%' }, splitLine: { lineStyle: { color: '#f1f5f9' } } },
    yAxis: {
      type: 'category',
      data: qtypeStats.map((q: any) => QTYPE_LABEL[q.type] || q.type),
      axisLabel: { fontSize: 11, color: '#555' },
    },
    series: [{
      type: 'bar', barWidth: 12,
      data: qtypeStats.map((q: any) => Math.round((q.rate || 0) * 100)),
      itemStyle: { color: '#8b5cf6', borderRadius: [0, 6, 6, 0] },
      label: { show: true, position: 'right', fontSize: 10, color: '#8b5cf6', formatter: '{c}%' },
    }],
  };

  return (
    <div className="page">
      <h1>学习统计</h1>
      <p style={{ color: 'var(--gray-500)', marginBottom: 16, fontSize: 13 }}>
        近 30 天有效学习记录
      </p>
      <div className="stats-grid">
        {items.map(it => (
          <div key={it.label} className="stat-card">
            <div className="stat-icon" style={{ background: `${it.color}15`, color: it.color }}>
              <Icon name={it.icon} size={20} />
            </div>
            <div className="stat-num">{it.value}</div>
            <div className="stat-label">{it.label}</div>
          </div>
        ))}
      </div>

      {/* 正确率环形仪表盘 */}
      <div className="stat-chart-card" style={{ background: '#fff', borderRadius: 12, padding: 16, marginTop: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>总体正确率</div>
        <ReactECharts option={gaugeOption} style={{ height: 150 }} notMerge lazyUpdate />
      </div>

      {/* 近30天学习趋势 */}
      {daily.length > 0 && (
        <div className="stat-chart-card" style={{ background: '#fff', borderRadius: 12, padding: 16, marginTop: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>近 30 天学习趋势</div>
          <ReactECharts option={trendOption} style={{ height: 180 }} notMerge lazyUpdate />
        </div>
      )}

      {/* 题型正确率 */}
      {qtypeOption && (
        <div className="stat-chart-card" style={{ background: '#fff', borderRadius: 12, padding: 16, marginTop: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>题型正确率</div>
          <ReactECharts option={qtypeOption} style={{ height: qtypeStats.length * 40 + 40 }} notMerge lazyUpdate />
        </div>
      )}

      {stats.totalQuestions != null && (
        <p style={{ marginTop: 24, color: 'var(--gray-500)', fontSize: 13, textAlign: 'center' }}>
          系统题库共 {stats.totalQuestions} 道题
        </p>
      )}
    </div>
  );
}
